import { TinyColor } from '@ctrl/tinycolor';
import { ConfigProvider, type ConfigProviderProps, Flex, Spin, Typography } from 'antd';
import { Inbox, LoaderCircle } from 'lucide-react';
import PropTypes from 'prop-types';
import React, { type ReactNode, useMemo, useState } from 'react';

import './style.css';

interface ThemeCustomizationProps {
  children: ReactNode;
}

interface ShadcnSeed {
  background: string;
  foreground: string;
  card: string;
  cardForeground: string;
  popover: string;
  popoverForeground: string;
  primary: string;
  primaryForeground: string;
  warning?: string;
  secondary: string;
  secondaryForeground: string;
  muted: string;
  mutedForeground: string;
  accent: string;
  accentForeground: string;
  destructive: string;
  destructiveForeground: string;
  border: string;
  borderSecondary: string;
  input: string;
  ring: string;
  radius: 7;
  chart1: string;
  chart2: string;
  chart3: string;
  chart4: string;
  chart5: string;
  shadow: string;
  popoverShadow: string;
}

const lightSeed: ShadcnSeed = {
  background: 'hsl(0 0% 100%)',
  foreground: 'hsl(0 0% 3.9%)',
  card: 'hsl(0 0% 100%)',
  cardForeground: 'hsl(0 0% 3.9%)',
  popover: 'hsl(0 0% 100%)',
  popoverForeground: 'hsl(0 0% 3.9%)',
  primary: 'hsl(0 0% 9%)',
  warning: 'hsl(26,100%,50%)',
  primaryForeground: 'hsl(0 0% 98%)',
  secondary: 'hsl(0 0% 96.1%)',
  secondaryForeground: 'hsl(0 0% 9%)',
  muted: 'hsl(0 0% 96.1%)',
  mutedForeground: 'hsl(0 0% 45.1%)',
  accent: 'hsl(0 0% 96.1%)',
  accentForeground: 'hsl(0 0% 9%)',
  destructive: 'hsl(0 84.2% 60.2%)',
  destructiveForeground: 'hsl(0 0% 98%)',
  border: 'hsl(0 0% 89.8%)',
  borderSecondary: 'hsl(220, 13.04%, 90.98%)',
  input: 'hsl(0 0% 89.8%)',
  ring: 'hsl(0 0% 3.9%)',
  radius: 7,
  chart1: 'hsl(12 76% 61%)',
  chart2: 'hsl(173 58% 39%)',
  chart3: 'hsl(197 37% 24%)',
  chart4: 'hsl(43 74% 66%)',
  chart5: 'hsl(27 87% 67%)',
  shadow:
    'rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 1px 2px 0px',
  popoverShadow:
    'rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0.1) 0px 4px 6px -1px, rgba(0, 0, 0, 0.1) 0px 2px 4px -2px',
};

const darkSeed: ShadcnSeed = {
  background: 'hsl(0 0% 3.9%)',
  foreground: 'hsl(0 0% 98%)',
  card: 'hsl(0 0% 3.9%)',
  cardForeground: 'hsl(0 0% 98%)',
  popover: 'hsl(0 0% 3.9%)',
  popoverForeground: 'hsl(0 0% 98%)',
  primary: 'hsl(0 0% 98%)',
  primaryForeground: 'hsl(0 0% 9%)',
  warning: 'hsl(26,100%,50%)',
  secondary: 'hsl(0 0% 14.9%)',
  secondaryForeground: 'hsl(0 0% 98%)',
  muted: 'hsl(0 0% 14.9%)',
  mutedForeground: 'hsl(0 0% 63.9%)',
  accent: 'hsl(0 0% 14.9%)',
  accentForeground: 'hsl(0 0% 98%)',
  destructive: 'hsl(0 62.8% 30.6%)',
  destructiveForeground: 'hsl(0 0% 98%)',
  border: 'hsl(0 0% 14.9%)',
  borderSecondary: 'hsl(240 3.7% 15.9%)',
  input: 'hsl(0 0% 14.9%)',
  ring: 'hsl(0 0% 83.1)',
  radius: 7,
  chart1: 'hsl(220 70% 50%)',
  chart2: 'hsl(160 60% 45%)',
  chart3: 'hsl(30 80% 55%)',
  chart4: 'hsl(280 65% 60%)',
  chart5: 'hsl(340 75% 55%)',
  shadow:
    'rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 1px 2px 0px',
  popoverShadow:
    'rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0.1) 0px 4px 6px -1px, rgba(0, 0, 0, 0.1) 0px 2px 4px -2px',
};

const loadTheme = (seedToken: ShadcnSeed): ConfigProviderProps['theme'] => ({
  hashed: false,
  cssVar: true,
  token: {
    colorWarning: seedToken.warning,
    borderRadius: seedToken.radius,
    colorBgContainer: seedToken.background,
    colorTextBase: seedToken.foreground,
    colorError: seedToken.destructive,
    colorBorder: seedToken.border,
    colorBorderSecondary: seedToken.borderSecondary,
    colorText: seedToken.foreground,
    colorErrorBg: 'transparent',
    colorTextSecondary: seedToken.secondary,
    colorPrimary: seedToken.primary,
    colorPrimaryText: seedToken.primaryForeground,
    colorInfo: seedToken.primary,
    colorPrimaryBg: seedToken.primary,
    colorTextDisabled: seedToken.mutedForeground,
    boxShadow: seedToken.shadow,
    boxShadowSecondary: seedToken.popoverShadow,
    boxShadowTertiary: seedToken.shadow,
    colorInfoBg: 'transparent',
    colorBgLayout: new TinyColor(seedToken.muted).setAlpha(0.4).toString(),
    colorBgElevated: seedToken.popover,
    colorBgSpotlight: seedToken.background,
    colorTextLightSolid: seedToken.background,
    colorTextPlaceholder: seedToken.mutedForeground,
    fontWeightStrong: 600,
    fontFamily:
      "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
  },
  components: {
    Button: {
      colorBgContainerDisabled: seedToken.secondary,
      colorTextDisabled: seedToken.mutedForeground,
      defaultShadow: seedToken.shadow,
      primaryShadow: seedToken.shadow,
      dangerShadow: seedToken.shadow,
      colorPrimaryBg: seedToken.primary,
      colorPrimaryText: seedToken.primaryForeground,
    },
    DatePicker: {
      cellActiveWithRangeBg: seedToken.accent,
    },
    Card: {
      headerBg: new TinyColor(seedToken.muted).setAlpha(0.5).toString(),
      actionsBg: new TinyColor(seedToken.muted).setAlpha(0.5).toString(),
    },
    Menu: {
      itemSelectedColor: seedToken.primaryForeground,
      itemSelectedBg: seedToken.primary,
      itemPaddingInline: 12,
      itemHeight: 36,
      itemMarginInline: 8,
      groupTitleLineHeight: 1.5,
      itemHoverBg: seedToken.accent,
      itemActiveBg: seedToken.accent,
      motionDurationFast: '0.1',
      motionDurationMid: '0.15',
      motionDurationSlow: '0.2',
    },
    Notification: {
      boxShadow: seedToken.popoverShadow,
    },
    Select: {
      optionSelectedColor: seedToken.background,
    },
    Calendar: {
      colorPrimary: seedToken.background,
    },
    Segmented: {
      trackBg: seedToken.accent,
      itemColor: seedToken.accentForeground,
      trackPadding: '0.25rem',
    },
    Layout: {
      headerBg: 'transparent',
      siderBg: seedToken.background,
      // bodyBg: seedToken.background,
    },
    Empty: {
      colorTextDescription: seedToken.mutedForeground,
    },
    Table: {
      headerBg: seedToken.background,
      headerColor: seedToken.mutedForeground,
      headerSplitColor: 'transparent',
      headerSortActiveBg: 'transparent',
      fixedHeaderSortActiveBg: 'transparent',
      bodySortBg: 'transparent',
      rowSelectedBg: seedToken.muted,
      rowSelectedHoverBg: seedToken.muted,
      fontWeightStrong: 500,
    },
    Alert: {
      colorSuccessBg: seedToken.background,
      colorWarningBg: seedToken.background,
    },
    Tag: {
      defaultBg: 'transparent',
    },
    Tooltip: {
      boxShadowSecondary: seedToken.popoverShadow,
      colorTextLightSolid: seedToken.foreground,
    },
    Pagination: {
      itemSizeSM: 38,
      colorBorder: seedToken.border,
    },
    Cascader: { optionSelectedBg: seedToken.input },
  },
});

Spin.setDefaultIndicator(
  <LoaderCircle
    style={{ animation: 'spin 0.7s linear infinite', width: '1.5em', height: '1.5em' }}
  />
);

const empty = (
  <Flex
    align="center"
    justify="center"
    style={{ placeItems: 'center', width: '100%', height: '100%', paddingBlock: '2.5rem' }}
  >
    <Flex gap="middle" align="center" justify="center" vertical>
      <Inbox style={{ width: '2rem', height: '2rem' }} />
      <Typography.Text style={{ fontSize: '1.25rem' }} disabled>
        No data
      </Typography.Text>
    </Flex>
  </Flex>
);

const ShadcnUiTheme = ({ children }: ThemeCustomizationProps): ReactNode => {
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;

  const [dark, setDark] = useState(prefersDark && false);
  const theme = useMemo(() => loadTheme(dark ? darkSeed : lightSeed), [dark]);

  return (
    <ConfigProvider
      componentSize="middle"
      wave={{ disabled: true }}
      notification={{ closeIcon: false }}
      renderEmpty={() => empty}
      // theme={{ cssVar: true, hashed: false }}
      theme={theme}
    >
      {children}
    </ConfigProvider>
  );
};

ShadcnUiTheme.propTypes = {
  children: PropTypes.node,
};

ShadcnUiTheme.defaultProps = {
  children: null,
};

export default ShadcnUiTheme;
